/**
 * Textarea Input Component
 * =======================
 * 
 * Form input template component for rendering textarea fields with intelligent suggestions.
 * This component extends basic textarea functionality with a custom autocomplete system
 * that remembers previously entered text and provides suggestions via a popover.
 * 
 * Core Features:
 * - Multi-line text input with configurable rows
 * - Intelligent text memory and suggestions system
 * - Custom suggestions popover (replaces native autocomplete)
 * - Form validation integration with error display
 * - Clear button for easy text removal
 * - Responsive design for mobile and desktop
 * - Spellcheck support and autocomplete/autocapitalize options
 * 
 * Memory System:
 * - Automatically saves entered text to localStorage
 * - Configurable minimum characters and maximum suggestions
 * - Smart filtering based on current input
 * - Persistent storage across browser sessions
 * - Automatic cleanup and error handling
 * 
 * Input Properties:
 * - templateFormGroup: FormGroup containing the form controls
 * - control: AbstractControl for the specific textarea field
 * - label: Field label with required indicator
 * - rows: Number of visible textarea rows
 * - enableMemory: Whether to enable the suggestions system
 * - maxSuggestions: Maximum number of saved suggestions
 * - minCharactersToSave: Minimum characters required to save
 * - showClearButton: Whether to show the clear button
 * 
 * Validation Features:
 * - Required field validation
 * - Length constraints (min/max)
 * - Pattern validation support
 * - Custom error messages for validation failures
 * - Touch state management for validation display
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { PopoverController } from '@ionic/angular';
import { SuggestionsPopoverComponent } from './suggestions-popover/suggestions-popover.component';
import { ScreenSizeService } from '../../../../../services/screen-size.service';   

@Component({
  selector: 'app-textarea-input',
  template: `
    <ng-container *ngIf="templateFormGroup && control" [formGroup]="templateFormGroup">
      <ion-label [position]="position" class="form-label">
        <strong>{{ label }}:</strong>
        <ion-text *ngIf="requiredField" color="danger">*</ion-text>
      </ion-label>
      
      <ion-item class="ion-no-padding" lines="none" class="form-input-border">
        <ion-textarea 
          [id]="controlName"
          #customInput
          [formControl]="formControl"
          [rows]="rows"
          [name]="controlName"
          [autocomplete]="autoComplete"
          [autocapitalize]="autoCapitalize"
          [fill]="fill"
          [placeholder]="placeholder"
          [class]="inputClass"
          [class.ion-invalid]="hasError"
          [class.ion-touched]="control.touched"
          [errorText]="getErrorMessage()"
          (ionFocus)="onFocus($event)"
          (ionBlur)="onBlur()"
          (ionChange)="onTextChange($event)"
          [spellcheck]="spellcheck">
        </ion-textarea>
        
        <!-- Clear button -->
        <ion-button 
          *ngIf="showClearButton && formControl.value"
          slot="end"
          fill="clear"
          size="small"
          (click)="clearTextarea($event)"
          class="clear-button">
          <ion-icon name="close-circle" color="medium"></ion-icon>
        </ion-button>
      </ion-item>
    </ng-container>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }

    /* Style invalid inputs */
    ion-textarea.ion-invalid.ion-touched {
      --border-color: var(--ion-color-danger);
      --color: var(--ion-color-danger);
    }
    



  `]
})
export class TextareaInputComponent implements OnChanges, OnInit {

  private activePopover: HTMLIonPopoverElement | null = null;
  public isMobile: boolean = false;

  @Input() templateFormGroup!: FormGroup;
  @Input() control!: AbstractControl | null;
  @Input() label!: string;
  @Input() requiredField: boolean = false;
  @Input() rows: number = 3;
  @Input() fill: string = 'none';
  @Input() placeholder: string = '';
  @Input() inputClass: string = 'custom-textarea';
  @Input() autoComplete: string = 'on';
  @Input() autoCapitalize: string = 'on';
  @Input() position: 'stacked' | 'fixed' | 'floating' = 'fixed';
  @Input() enableMemory: boolean = true;
  @Input() maxSuggestions: number = 10;
  @Input() minCharactersToSave: number = 1;
  @Input() showClearButton: boolean = true;
  @Input() spellcheck: boolean = true;

  controlName: string = '';
  formControl: FormControl = new FormControl();
  
  // Memory-related properties
  savedTexts: string[] = [];
  filteredSuggestions: string[] = [];
  private memoryKey: string = '';
  private isShowingPopover: boolean = false;
  private shouldSkipNextFocus: boolean = false;  

  constructor(
    private popoverController: PopoverController,
    private screenSizeService: ScreenSizeService
  ) {
    this.isMobile = this.screenSizeService.isMobileDevice();
  }

  /**
   * Computed property to check if control has errors and is touched.
   * Used to determine when to show error states and styling.
   * 
   * @returns True if the control has validation errors and has been touched
   */
  get hasError(): boolean {
    return !!(this.control?.invalid && this.control?.touched);
  }

  /**
   * Gets the appropriate error message based on validation errors.
   * Provides user-friendly error messages for different validation failures.
   * 
   * @returns String containing the appropriate error message
   */
  getErrorMessage(): string {
    if (!this.hasError || !this.control?.errors) {
      return '';
    }

    const errors = this.control.errors;

    if (errors['required']) {
      return `Field is required`;
    }
    if (errors['minlength']) {
      return `Field must be at least ${errors['minlength'].requiredLength} characters`;
    }
    if (errors['maxlength']) {
      return `Field must be no more than ${errors['maxlength'].requiredLength} characters`;
    }
    if (errors['pattern']) {
      return `Field format is invalid`;
    }

    return `${this.label} is invalid`;
  }

  /**
   * Lifecycle hook that is called after data-bound properties are initialized.
   * Sets up the control name, generates memory key, and loads saved suggestions.
   */
  ngOnInit(): void {
    if (this.control) {
      // Get the control name from the form group
      this.controlName = Object.keys(this.templateFormGroup.controls).find(
        key => this.templateFormGroup.get(key) === this.control
      ) || '';
      
      // Generate memory key
      this.memoryKey = `textarea_memory_${this.controlName || this.label.toLowerCase().replace(/\s/g, '_')}`;
      
      // Load saved suggestions if memory is enabled
      if (this.enableMemory) {
        this.loadSavedTexts();
      }
    }
  }

  /**
   * Lifecycle hook that responds to input property changes.
   * Handles changes to templateFormGroup and control properties.
   * 
   * @param changes - Object containing information about changed properties
   */
  ngOnChanges(changes: SimpleChanges): void {
    if ('templateFormGroup' in changes) {
      this.templateFormGroup = changes['templateFormGroup'].currentValue;
    }
    if ('control' in changes && this.control) {
      this.controlName = Object.keys(this.templateFormGroup.controls).find(
        key => this.templateFormGroup.get(key) === this.control
      ) || '';
      this.formControl = this.control as FormControl;
    }
  }

  /**
   * Loads saved text suggestions from localStorage.
   * Handles JSON parsing errors and ensures the savedTexts array is valid.
   * 
   * @private
   */
  private loadSavedTexts(): void {
    try {
      const saved = localStorage.getItem(this.memoryKey);
      if (saved) {
        this.savedTexts = JSON.parse(saved);
        if (!Array.isArray(this.savedTexts)) {
          this.savedTexts = [];
        }
      }
    } catch (error) {
      console.error('Error loading saved texts:', error);
      this.savedTexts = [];
    }
  }

  /**
   * Handles focus events on the textarea to show suggestions.
   * Filters suggestions based on current input and shows the popover.
   * Includes logic to prevent multiple popovers and handle focus skipping.
   * 
   * @param event - The focus event object
   */
  async onFocus(event: any) {
    // Skip if already showing or should skip
    if (this.isShowingPopover || this.shouldSkipNextFocus) {
      this.shouldSkipNextFocus = false;
      return;
    }
    
    // Skip if no saved texts or if field has value
    if (!this.enableMemory || this.savedTexts.length === 0 || this.formControl.value) return;
    
    // Filter suggestions immediately
    this.filterSuggestions(this.formControl.value || '');
    if (this.filteredSuggestions.length === 0) return;
    
    // Show popover
    await this.showSuggestionsPopover(event);
  }
  
  /**
   * Shows the suggestions popover with appropriate positioning and styling.
   * Configures different popover options for mobile and desktop devices.
   * Handles popover dismissal and suggestion selection.
   * 
   * @param event - The event that triggered the popover
   * @private
   */
  private async showSuggestionsPopover(event: any) {
    this.isShowingPopover = true;
    
    const popoverOptions: any = {
      component: SuggestionsPopoverComponent,
      componentProps: {
        suggestions: [...this.filteredSuggestions],
        onDelete: (suggestion: string) => this.handleDeleteFromPopover(suggestion)
      },
      showBackdrop: this.isMobile,
      dismissOnSelect: false,
      animated: true,
      translucent: false,
      cssClass: this.isMobile ? 'suggestions-popover-mobile' : 'suggestions-popover-desktop'
    };
    
    if (!this.isMobile) {
      popoverOptions.event = event;
      popoverOptions.showArrow = true;
      popoverOptions.alignment = 'start';
      popoverOptions.side = 'bottom';
      
      
    } 

    if (this.isMobile) {
      popoverOptions.cssClass = 'suggestions-popover-mobile';
      popoverOptions.event = event;
      popoverOptions.side = 'top';
      popoverOptions.alignment = 'start';
      popoverOptions.size = 'cover';  // Makes it match trigger width
    }
    this.activePopover = await this.popoverController.create(popoverOptions);
    await this.activePopover.present();
    
    const { data } = await this.activePopover.onDidDismiss();
    this.isShowingPopover = false;
    this.activePopover = null;
    
    if (data?.selected) {
      this.formControl.setValue(data.selected);
    } else if (!this.formControl.value) {
      this.shouldSkipNextFocus = true;
      const textarea = document.getElementById(this.controlName);
      if (textarea) {
        (textarea as HTMLIonTextareaElement).setFocus();
      }
    }
  }

  /**
   * Handles deletion of suggestions from the popover.
   * Updates both the saved texts and filtered suggestions arrays.
   * Saves the updated suggestions to localStorage.
   * 
   * @param suggestion - The suggestion to delete
   * @private
   */
  private handleDeleteFromPopover(suggestion: string) {
    // Update saved texts
    this.savedTexts = this.savedTexts.filter(text => text !== suggestion);
    this.filteredSuggestions = this.filteredSuggestions.filter(text => text !== suggestion);
    
    // Save to localStorage
    this.saveToLocalStorage();
  }

  /**
   * Handles blur events on the textarea.
   * Saves the current value and dismisses any open popover.
   * Includes a delay to prevent immediate dismissal during popover interaction.
   */
  onBlur(): void {
    this.saveCurrentValue();
    if (this.isShowingPopover) return; 
    // Dismiss popover on blur if still open
    setTimeout(async () => {
      if (this.activePopover) {
        await this.activePopover.dismiss();
        this.activePopover = null;
        this.isShowingPopover = false;  // ADD THIS LINE
      }
    }, 200);
  }

  /**
   * Handles text change events in the textarea.
   * Filters suggestions based on the current input value.
   * 
   * @param event - The ionChange event object
   */
  onTextChange(event: any): void {
    const value = event.detail.value || '';
    this.filterSuggestions(value);
  }

  /**
   * Filters suggestions based on the current input value.
   * Shows all saved suggestions if no input, or filters by partial matches.
   * 
   * @param value - The current textarea value to filter by
   * @private
   */
  private filterSuggestions(value: string): void {
    if (!value) {
      this.filteredSuggestions = [...this.savedTexts];
    } else {
      const searchValue = value.toLowerCase();
      this.filteredSuggestions = this.savedTexts.filter(text =>
        text.toLowerCase().includes(searchValue)
      );
    }
  }

  /**
   * Saves the current textarea value to the suggestions list.
   * Only saves if memory is enabled, value meets minimum length, and isn't already saved.
   * Maintains the maximum suggestions limit by removing oldest entries.
   * 
   * @private
   */
  private saveCurrentValue(): void {
    if (!this.enableMemory) return;
    
    const value = this.formControl.value;
    
    if (value && 
        value.length >= this.minCharactersToSave && 
        !this.savedTexts.includes(value)) {
      
      this.savedTexts.unshift(value);
      this.savedTexts = this.savedTexts.slice(0, this.maxSuggestions);
      
      this.saveToLocalStorage();
    }
  }

  /**
   * Clears the textarea content and refocuses the field.
   * Prevents event propagation and provides immediate user feedback.
   * 
   * @param event - The click event object
   */
  clearTextarea(event: Event): void {
    event.stopPropagation();
    this.formControl.setValue('');
    
    // Focus back on textarea after clearing
    setTimeout(() => {
      const textarea = document.getElementById(this.controlName);
      if (textarea) {
        (textarea as HTMLIonTextareaElement).setFocus();
      }
    }, 50);
  }

  /**
   * Saves the current suggestions to localStorage.
   * Includes error handling for storage quota exceeded errors.
   * Automatically reduces suggestions if storage fails and retries.
   * 
   * @private
   */
  private saveToLocalStorage(): void {
    try {
      localStorage.setItem(this.memoryKey, JSON.stringify(this.savedTexts));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      
      if (error instanceof DOMException && error.code === 22) {
        this.savedTexts = this.savedTexts.slice(0, Math.floor(this.maxSuggestions / 2));
        try {
          localStorage.setItem(this.memoryKey, JSON.stringify(this.savedTexts));
        } catch (retryError) {
          console.error('Failed to save even after clearing entries:', retryError);
        }
      }
    }
  }
}